<script setup lang="ts">
import { computed, ref } from 'vue'
import { simulatorMockup, simulatorTabs, simulators } from '../data/landing.content'
import { useLandingScroll } from '../composables/useLandingScroll'
import LandingSimulatorWhatIs from './LandingSimulatorWhatIs.vue'
import LandingSimulatorCapabilities from './LandingSimulatorCapabilities.vue'
import WlIcon from './WlIcon.vue'

const { registerReveal } = useLandingScroll()
const activeTab = ref<string>('todos')

/**
 * El bloque "Catálogo" (tabs por destino + grid de tarjetas) esta oculto
 * temporalmente mientras se revisa la oferta de simuladores. El componente se
 * conserva intacto: basta con poner este flag en `true` para volver a
 * publicarlo. El encabezado de la seccion y el bloque explicador siguen
 * visibles, de modo que el ancla `#simuladores` (navbar, footer y CTA)
 * continúa resolviendo a esta seccion.
 */
const showCatalog = false

/**
 * El catálogo se filtra por destino ("¿a dónde quieres entrar?"). La tarjeta
 * informativa de "próximamente" no pertenece a ningún destino, así que solo
 * aparece en la vista completa.
 */
const visibleSimulators = computed(() =>
  activeTab.value === 'todos'
    ? simulators
    : simulators.filter((simulator) => simulator.destino === activeTab.value)
)

function selectTab(key: string) {
  activeTab.value = key
}
</script>

<template>
  <section class="wl-simuladores-section" id="simuladores">
    <div class="container">
      <div class="section-head reveal" :ref="registerReveal">
        <div class="section-tag">Simuladores</div>
        <h2 class="section-title">Practica para <span class="gradient-text">tu examen</span></h2>
        <p class="section-desc">
          Entiende qué es un simulador y qué puedes hacer con él para prepararte antes de tu examen.
        </p>
      </div>

      <!-- Bloque A: encabezado "qué es" a ancho completo -->
      <LandingSimulatorWhatIs part="head" />

      <!-- Showcase: definiciones (vertical) + mockup de la pantalla de practica -->
      <div class="wl-sim-showcase reveal" :ref="registerReveal">
        <div class="wl-sim-showcase-side">
          <LandingSimulatorWhatIs part="cards" />
        </div>

        <div class="wl-sim-showcase-main">
          <!-- Mockup: la pantalla de practica de un simulador -->
          <div class="wl-browser-mockup wl-browser-mockup--sim">
            <div class="wl-bm-chrome">
              <div class="wl-bm-dots">
                <span class="wl-bd-r"></span>
                <span class="wl-bd-y"></span>
                <span class="wl-bd-g"></span>
              </div>
              <div class="wl-bm-url">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                {{ simulatorMockup.url }}
              </div>
              <div class="wl-bm-ctrl">
                <span></span><span></span><span></span>
              </div>
            </div>

            <div class="wl-sim-mock-body">
              <!-- Encabezado + secciones del temario -->
              <div class="wl-sim-mock-side">
                <div class="wl-sim-mock-head">
                  <span class="wl-sim-mock-head-icon">
                    <WlIcon name="focus-target" />
                  </span>
                  <div class="wl-sim-mock-head-text">
                    <h3>{{ simulatorMockup.headerTitle }}</h3>
                    <p>{{ simulatorMockup.headerSubtitle }}</p>
                  </div>
                </div>

                <div class="wl-sim-mock-timer">
                  <WlIcon name="clock" />
                  <span>{{ simulatorMockup.timerLabel }}</span>
                </div>

                <span class="wl-sim-mock-sections-label">{{ simulatorMockup.sectionsLabel }}</span>

                <ul class="wl-sim-mock-sections">
                  <li
                    v-for="section in simulatorMockup.sections"
                    :key="section.name"
                    class="wl-sim-mock-section"
                    :class="`wl-sim-mock-section--${section.status}`"
                  >
                    <span class="wl-sim-mock-section-icon">
                      <WlIcon
                        :name="section.status === 'done' ? 'check-circle' : section.status === 'current' ? 'play' : 'clock'"
                      />
                    </span>
                    <span class="wl-sim-mock-section-name">{{ section.name }}</span>
                    <span class="wl-sim-mock-section-status">{{ section.statusLabel }}</span>
                  </li>
                </ul>
              </div>

              <!-- Pregunta en curso -->
              <div class="wl-sim-mock-question">
                <div class="wl-sim-mock-block">
                  <h4 class="wl-sim-mock-block-title">
                    <WlIcon name="comment-question" />
                    {{ simulatorMockup.questionTitle }}
                  </h4>
                  <p class="wl-sim-mock-question-text">{{ simulatorMockup.questionText }}</p>
                </div>

                <div class="wl-sim-mock-block">
                  <h4 class="wl-sim-mock-block-title">
                    <WlIcon name="info-circle" />
                    {{ simulatorMockup.instructionTitle }}
                  </h4>
                  <p class="wl-sim-mock-instruction">{{ simulatorMockup.instructionText }}</p>
                </div>

                <div class="wl-sim-mock-block">
                  <h4 class="wl-sim-mock-block-title">
                    <WlIcon name="check-square" />
                    {{ simulatorMockup.answersTitle }}
                  </h4>
                  <ul class="wl-sim-mock-answers">
                    <li
                      v-for="answer in simulatorMockup.answers"
                      :key="answer.label"
                      class="wl-sim-mock-answer"
                      :class="{ 'wl-sim-mock-answer--selected': answer.selected }"
                    >
                      <span class="wl-sim-mock-answer-box">
                        <WlIcon v-if="answer.selected" name="check" />
                      </span>
                      <span class="wl-sim-mock-answer-label">{{ answer.label }}</span>
                      <span class="wl-sim-mock-answer-text">{{ answer.text }}</span>
                    </li>
                  </ul>
                </div>

                <div class="wl-sim-mock-nav">
                  <span class="wl-sim-mock-nav-btn wl-sim-mock-nav-btn--ghost">
                    {{ simulatorMockup.prevLabel }}
                  </span>
                  <span class="wl-sim-mock-nav-btn wl-sim-mock-nav-btn--primary">
                    {{ simulatorMockup.nextLabel }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LandingSimulatorCapabilities />

      <!-- Bloque C: catálogo por destino -->
      <div v-if="showCatalog" class="wl-sim-block wl-sim-block--spaced wl-sim-block--catalog">
        <div class="wl-sim-block-head reveal" :ref="registerReveal">
          <span class="section-tag">Catálogo</span>
          <h3 class="wl-sim-block-title">
            Elige según <span class="gradient-text">a dónde quieres entrar</span>
          </h3>
          <p class="wl-sim-block-desc">
            Cada simulador corresponde a un examen real. Elige por tu destino, no por la sigla.
          </p>
        </div>

        <div class="wl-tabs-wrap reveal" :ref="registerReveal">
          <div class="wl-tabs" role="tablist">
            <button
              v-for="tab in simulatorTabs"
              :key="tab.key"
              class="wl-tab"
              :class="{ 'wl-is-active': activeTab === tab.key }"
              role="tab"
              :aria-selected="activeTab === tab.key"
              type="button"
              @click="selectTab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <div class="wl-sim-grid">
          <article
            v-for="(simulator, index) in visibleSimulators"
            :key="simulator.id"
            class="wl-sim-card reveal"
            :class="[
              { 'wl-sim-card--featured': simulator.featured, 'wl-sim-card--placeholder': simulator.placeholder },
              `reveal-d${(index % 4) + 1}`
            ]"
            :ref="registerReveal"
          >
            <span class="wl-exam-tag" :class="simulator.tagClass">{{ simulator.tag }}</span>
            <h4>{{ simulator.title }}</h4>
            <p class="wl-sim-glossary">{{ simulator.glossary }}</p>
            <p class="wl-sim-audience">
              <template v-if="simulator.placeholder">Lo que hacemos: </template>
              <template v-else><b>Para quién es:</b> </template>
              {{ simulator.audience }}
            </p>
            <div v-if="simulator.chips.length" class="wl-sim-chips">
              <span v-for="chip in simulator.chips" :key="chip" class="wl-sim-chip">{{ chip }}</span>
            </div>
            <a class="wl-sim-btn" href="/estudiante/simuladores">
              {{ simulator.placeholder ? 'Ver todos' : 'Ver simulador' }}
              <WlIcon name="arrow-right" />
            </a>
          </article>
        </div>

        <div class="section-cta reveal" :ref="registerReveal">
          <a href="/estudiante/simuladores" class="btn btn-primary">Ver todos los simuladores</a>
        </div>
      </div>
    </div>
  </section>
</template>
